
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { ArxivRecord, ArxivSearchParams } from '../types';
import { getYesterdayDate, getTodayDate } from '../utils';
import { fetchArxivCache, isWithinCacheWindow, getCacheWindow } from '@/lib/api/arxiv-cache';

export const useArxivSearch = () => {
  const [searchParams, setSearchParams] = useState<ArxivSearchParams>({
    set: 'cs',
    from: getYesterdayDate(),
    until: getTodayDate()
  });
  const [records, setRecords] = useState<ArxivRecord[]>([]);
  const [resumptionToken, setResumptionToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [cacheWindow, setCacheWindow] = useState(getCacheWindow());

  const handleSearch = async (useResumptionToken?: string) => {
    setLoading(true);
    
    try {
      // Check if this is a resumption token request (always use live fetch)
      if (useResumptionToken) {
        const { data, error } = await supabase.functions.invoke('fetch-arxiv-metadata', {
          body: {
            resumptionToken: useResumptionToken,
            metadataPrefix: 'arXiv'
          }
        });

        if (error) throw error;

        setRecords(prev => [...prev, ...data.records]);
        setResumptionToken(data.resumptionToken);
        toast.success(`Fetched ${data.records.length} additional records from arXiv`);
        return;
      }

      // Determine if we should use cache or live fetch
      const shouldUseCache = isWithinCacheWindow(searchParams.from, searchParams.until);
      
      if (shouldUseCache) {
        // Use cache
        const result = await fetchArxivCache({
          set: searchParams.set || undefined,
          from: searchParams.from || undefined,
          until: searchParams.until || undefined,
          limit: 1000 // Cache should have all data for the window
        });

        setRecords(result.records);
        setResumptionToken(undefined); // No resumption token for cache
        setIsFromCache(true);
        setHasSearched(true);
        toast.success(`Loaded ${result.records.length} records from cache`);
      } else {
        // Use live fetch
        const { data, error } = await supabase.functions.invoke('fetch-arxiv-metadata', {
          body: {
            set: searchParams.set || undefined,
            from: searchParams.from || undefined,
            until: searchParams.until || undefined,
            metadataPrefix: 'arXiv'
          }
        });

        if (error) throw error;

        setRecords(data.records);
        setResumptionToken(data.resumptionToken);
        setIsFromCache(false);
        setHasSearched(true);
        toast.success(`Fetched ${data.records.length} records from arXiv`);
      }
    } catch (error) {
      console.error('Error fetching arXiv metadata:', error);
      toast.error('Failed to fetch metadata');
      setIsFromCache(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (resumptionToken) {
      handleSearch(resumptionToken);
    }
  };

  return {
    searchParams,
    setSearchParams,
    records,
    resumptionToken,
    loading,
    hasSearched,
    isFromCache,
    cacheWindow,
    handleSearch,
    handleLoadMore
  };
};
